import { useMutation } from '@tanstack/react-query';

import { CreateDroneHarmonyFlightPlanParameters } from '../../../../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../../../../services/axios/skymap-axios-service-factory';
import { RequestParamsToApiPaths } from '../../../../../../../utils/typescript-utils';
import { useErrorHandling } from '../../../../../../hooks/use-error-handling';
import { GcpRow } from '../../../../../cloud-processing/gcp-parser';
import { FormValues } from './add-flight-plan-dialog';

type Props = {
  projectId: string;
  gcpFileContent: GcpRow[];
  boundaryPolygonUploadIds: string[];
  onSuccess: () => void;
};

const useSaveQuery = (props: Props) => {
  const {
    clearErrors: clearApiErrors,
    getErrorForPath,
    handleError,
    buildErrorList,
    hasError,
    excludeErrorPaths,
  } = useErrorHandling<RequestParamsToApiPaths<CreateDroneHarmonyFlightPlanParameters>>();

  const saveQuery = useMutation({
    mutationFn: (form: FormValues) =>
      SkyMapAxiosServiceFactory.instance.createProjectServiceV1().createDroneHarmonyFlightPlan({
        body: {
          package:
            form.geodataProcessType === 'ORTHO_ONLY'
              ? {
                  geodataProcessType: 'ORTHO_ONLY',
                }
              : {
                  generateTerrainModel: form.generateTerrainModel,
                  geodataProcessType: form.geodataProcessType,
                },
          missionId: form.missionId,
          groundControlPoints: props.gcpFileContent.map((x) => ({
            easting: parseFloat(x.e.value),
            height: parseFloat(x.h.value),
            name: x.name.value,
            northing: parseFloat(x.n.value),
          })),
          boundaryPolygonUploadIds: props.boundaryPolygonUploadIds,
        },
        path: {
          projectId: props.projectId,
        },
      }),
    retry: false,
    networkMode: 'always',
    onSuccess: () => props.onSuccess(),
    onMutate: () => clearApiErrors(),
    onError: (err) => handleError(err),
  });

  const errorList =
    hasError(excludeErrorPaths(['body.package', 'body.groundControlPoints', 'body.missionId'])) &&
    buildErrorList({
      filter: excludeErrorPaths(['body.package', 'body.groundControlPoints', 'body.missionId']),
    });

  return {
    saveQuery,
    getErrorForPath,
    errorList,
  };
};

export { useSaveQuery };
